import { useState, ReactNode, useRef, useEffect } from 'react';

export type HelpItem = {
    id: string;
    title: string;
    content: ReactNode;
};

type Props = {
    item: HelpItem;
    defaultOpen?: boolean;
};

export default function CollapsibleHelpItem({ item, defaultOpen = false }: Props) {
    const [open, setOpen] = useState<boolean>(defaultOpen);
    const contentRef = useRef<HTMLDivElement | null>(null);
    const innerRef = useRef<HTMLDivElement | null>(null);
    const [maxHeight, setMaxHeight] = useState<string>(defaultOpen ? 'none' : '0px');

    useEffect(() => {
        const el = contentRef.current;
        const inner = innerRef.current;
        if (!el || !inner) return;

        if (open) {
            // Expand: measure content height and animate from 0 → scrollHeight.
            const scrollH = inner.scrollHeight;
            setMaxHeight(`${scrollH}px`);

            // Once the opening transition finishes, release the height constraint so
            // the content can grow freely (e.g. if images load and change height).
            const handleTransitionEnd = (e: TransitionEvent) => {
                if (e.propertyName === 'max-height') {
                    setMaxHeight('none');
                }
            };

            el.addEventListener('transitionend', handleTransitionEnd as any);

            // IMPORTANT: always clean up so a stale listener never fires during a
            // later close animation and accidentally re-opens the item.
            return () => el.removeEventListener('transitionend', handleTransitionEnd as any);
        } else {
            // Collapse: give the element a concrete pixel height first so the CSS
            // transition has a defined start value, then animate down to 0.
            const scrollH = inner.scrollHeight;
            setMaxHeight(`${scrollH}px`);

            let rAF1: number;
            let rAF2: number;
            rAF1 = requestAnimationFrame(() => {
                rAF2 = requestAnimationFrame(() => setMaxHeight('0px'));
            });

            // Cancel pending frames if the user toggles again mid-animation.
            return () => {
                cancelAnimationFrame(rAF1);
                cancelAnimationFrame(rAF2);
            };
        }
    }, [open]);

    return (
        <div className={`collapsible-help-item ${open ? 'is-open' : ''}`}>
            <button
                type="button"
                className="collapsible-help-btn"
                onClick={() => setOpen((s) => !s)}
                aria-expanded={open}
                aria-controls={`help-content-${item.id}`}
            >
                <span className="collapsible-help-title">{item.title}</span>
                <span className="collapsible-help-indicator" aria-hidden>
                    {open ? '▾' : '▸'}
                </span>
            </button>

            <div
                id={`help-content-${item.id}`}
                ref={contentRef}
                className="collapsible-help-content"
                style={{ '--max-height': maxHeight } as React.CSSProperties}
            >
                <div ref={innerRef} className="collapsible-help-inner">
                    {item.content}
                </div>
            </div>
        </div>
    );
}
